import { useCallback, useEffect, useRef, useState } from 'react';

const ACTIVE_MISSION_POLL_INTERVAL_MS = 2000;

export default function useActiveRobotMission(apiConfig, robotId) {
  const apiConfigRef = useRef(apiConfig);
  const robotIdRef = useRef(robotId);
  const mountedRef = useRef(true);
  const [activeMission, setActiveMission] = useState(null);
  const [loading, setLoading] = useState(Boolean(robotId));
  const [error, setError] = useState('');
  const [controlAction, setControlAction] = useState('');

  apiConfigRef.current = apiConfig;
  robotIdRef.current = robotId;

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    const currentRobotId = robotIdRef.current;
    if (!currentRobotId) {
      if (mountedRef.current) {
        setActiveMission(null);
        setLoading(false);
        setError('');
      }
      return null;
    }

    if (!silent && mountedRef.current) setLoading(true);
    try {
      const response = await apiConfigRef.current.fetchActiveMission(currentRobotId);
      const mission = response?.RobotResponse || null;
      if (mountedRef.current && currentRobotId === robotIdRef.current) {
        setActiveMission(mission);
        setError('');
      }
      return mission;
    } catch (requestError) {
      if (mountedRef.current && currentRobotId === robotIdRef.current) {
        setError(
          requestError?.response?.data?.Message
          || requestError.message
          || 'Mission state is unavailable'
        );
      }
      return null;
    } finally {
      if (!silent && mountedRef.current && currentRobotId === robotIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    setActiveMission(null);
    setError('');
    setLoading(Boolean(robotId));
    if (!robotId) return undefined;

    refresh();
    const intervalId = window.setInterval(
      () => refresh({ silent: true }),
      ACTIVE_MISSION_POLL_INTERVAL_MS
    );
    return () => window.clearInterval(intervalId);
  }, [robotId, refresh]);

  const controlMission = useCallback(async (action) => {
    const currentRobotId = robotIdRef.current;
    if (!currentRobotId || controlAction) return null;

    setControlAction(action);
    setError('');
    try {
      const response = await apiConfigRef.current.controlActiveMission(currentRobotId, action);
      await refresh({ silent: true });
      return response?.RobotResponse || null;
    } catch (requestError) {
      if (mountedRef.current) {
        setError(
          requestError?.response?.data?.Message
          || requestError.message
          || `Unable to ${action} mission`
        );
      }
      return null;
    } finally {
      if (mountedRef.current) setControlAction('');
    }
  }, [controlAction, refresh]);

  return {
    activeMission,
    loading,
    error,
    controlAction,
    refresh,
    controlMission,
  };
}
